import { createSelector } from 'reselect';

import { GlobalState } from '../../../types.global';
import { Friend, Message, SendingState, SortByOptions } from './types';

import { selectLoggedUser } from '../../user/slice';
import { timeInMs } from '../../../../shared';

export const selectInviteLink = (state: GlobalState): string => {
  const loggedUser = selectLoggedUser(state);
  if (!loggedUser) return null;
  const appUrl =
    process.env.APP_PLATFORM === 'web' ? location.origin : process.env.APP_URL;
  return `${appUrl}/j/${loggedUser.id}`;
};

const selectIsUserMuted = (state: GlobalState): boolean => {
  const user = selectLoggedUser(state);
  return (
    user &&
    (user.mutedMessagesUntil === -1 ||
      (user.mutedMessagesUntil || 0) > Date.now())
  );
};

const selectFriends = (state: GlobalState) => state.friends.friends.list;

export const selectFriendsData = createSelector(
  selectFriends,
  (state: GlobalState) => state.friends.$realtime,
  (friends, $realtime): Friend[] =>
    friends.map((friend) => ({
      ...friend,
      isOnline: (() => {
        if ($realtime.isOnlineById[friend.id]) return true;
        if (!friend.updatedAt) return false;
        const now = Date.now();
        return Number(new Date(friend.updatedAt)) > now - timeInMs.minute * 5;
      })(),
    }))
);

export const selectNewFriend = (state: GlobalState): Friend => {
  const { newFriendsIds } = state.friends.friends;
  return newFriendsIds.length
    ? selectFriends(state).find(({ id }) => newFriendsIds[0] === id)
    : null;
};

const EMPTY_MESSAGES_LIST = [];
const selectMessagesList = (state: GlobalState) =>
  selectIsUserMuted(state) ? EMPTY_MESSAGES_LIST : state.friends?.messages.list;

const selectUnreadIds = (state: GlobalState) => state.friends?.messages.unreadIds;

export const selectUnreadMessages = createSelector(
  selectMessagesList,
  selectUnreadIds,
  (messages= [], unreadIds = []) => messages
    .filter(({ id }) => (unreadIds).includes(id))
    .reverse()
);

export const selectReadNotReceivedMessages = createSelector(
  selectLoggedUser,
  selectMessagesList,
  selectUnreadIds,
  (user, messages= [], unreadIds= []) => messages.filter(
    ({ id, received, friendTo }) =>
      !received &&
      !unreadIds.includes(id) &&
      user?.id === friendTo
  )
);

export const selectSentMessages = createSelector(
  selectLoggedUser,
  selectMessagesList,
  (user, messages): Message[] => messages.filter(
    ({ friendFrom }) => friendFrom === user.id
  )
);

// export const selectSortBy = ({ friends }) => friends.friends.sortBy;
export const selectSortBy = (): SortByOptions => 'shellsCounter';

export const selectShowAll = ({ friends }: GlobalState): boolean => friends.friends.showAll;

export const selectIsHiddenFriends = ({ friends }: GlobalState): boolean => friends.friends.isHidden;

export const selectSendToFriend = ({
  friends: {
    friends: { list, sendTo },
  },
}: GlobalState): Friend => list.find(({ id }) => id === sendTo);

const DEFAULT_SENDING_STATE = { type: null };

export const selectSendingState = (state: GlobalState): SendingState => {
  if (selectIsUserMuted(state)) return { type: 'muted' };
  return state.friends.friends.sendingState || DEFAULT_SENDING_STATE;
};

export const selectIsOnboarded = ({ friends }: GlobalState): boolean => friends.isOnboarded;

export const selectIsViewed = ({ friends }: GlobalState): boolean => friends.isViewed;

export const selectOpenedPopover = (state: GlobalState): string => state.friends.openedHeaderPopover;
